/**
 * Wire reconciliation calculation utilities for Banker Recon.
 *
 * Legacy warehouse advance math accounts for:
 *   1. Fees retained at closing (processing, UW, prepaid interest, known retained fees, escrow, etc.)
 *   2. Government funding pass-throughs (VA Funding Fee, FHA UFMIP, USDA Guarantee Fee)
 *   3. Temporary subsidy / interest-rate buydown pass-throughs
 *   4. Lender credit/cure rows explicitly netted to the warehouse advance
 *   5. Non-QM haircut percentage (applied on top of fee-based advance)
 *
 * Legacy formula:
 *   feesAtClosing   = retainedClosingFees + escrowDeposit + governmentFundingPassThroughs + buydownPassThroughs
 *   netFeesRetained = max(0, feesAtClosing - creditCureNettedToWarehouseAdvance)
 *   warehouseAdvance = loanAmount × (1 − haircut%) − netFeesRetained
 *   expectedBalance  = wireFromInvestor − warehouseAdvance − warehouseCosts
 *                      − shortageFunded + flexcloseCredit
 *
 * Opt-in CD cash-required mode is used only when no explicit shortage/haircut basis
 * is present. It preserves a signed CD fee block:
 *   netPayableToLenderAtClosing = feesAtClosing − cdCreditNettedToWarehouseAdvance
 *   cashNeededAtClosing = loanAmount − netPayableToLenderAtClosing
 *   expectedBalance = wireFromInvestor − cashNeededAtClosing − warehouseCosts
 *                     + flexcloseCredit
 */

import { isBuydownPassThrough } from './buydownPassThrough';
import { manualWireAdjustment } from './manualAdjustments';

export interface WarehouseCashFlowLineItem {
  category: string;
  line_type: string;
  description: string;
  amount: number;
  reimbursable?: boolean | null;
  nets_to_warehouse_advance?: boolean | null;
  /** Retained-at-warehouse amount excluded from P&L (warehouse basis = amount + this). */
  warehouse_retained_amount?: number | null;
}

export type RetainedClosingFeeBasis = 'legacy' | 'expandedLeFees';

const LEGACY_RETAINED_CLOSING_FEE_LINE_TYPES = new Set([
  'appraisal_fee',
  'credit_report',
  'section_a_appraisal_fee',
  'section_a_credit_report',
  'section_a_appraisal_review_fee',
  'section_a_loan_discount',
  'loan_discount_seller_paid',
  'section_a_reinspection_fee',
  'section_a_trust_certification_fee',
  'section_a_trust_review_fee',
  'section_a_verification_fee'
]);

const EXPANDED_LE_FEE_RETAINED_CLOSING_FEE_LINE_TYPES = new Set([
  ...LEGACY_RETAINED_CLOSING_FEE_LINE_TYPES,
  'section_a_certificate_of_insurance',
  'section_a_hoa_certification_fee'
]);

const GOVERNMENT_LOAN_TYPES = new Set(['VA', 'FHA', 'USDA']);
const MONEY_TOLERANCE = 0.01;

export type WarehouseBasisMode = 'legacy' | 'autoCdCashRequired';
export type WarehouseBasisModeUsed = 'legacy' | 'cdCashRequired';

function normalizeText(value: string | null | undefined): string {
  return (value || '').trim();
}

function toFiniteNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = String(value).replace(/[$,\s]/g, '');
  if (!cleaned) {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isGovernmentBackedLoanType(loanType: string | null | undefined): boolean {
  return GOVERNMENT_LOAN_TYPES.has(normalizeText(loanType).toUpperCase());
}

export function isGovernmentFundingPassThrough(
  item: Pick<WarehouseCashFlowLineItem, 'category' | 'line_type' | 'description'>,
  loanType?: string | null
): boolean {
  if (item.category !== 'investor_cost') {
    return false;
  }

  if (isBuydownPassThrough(item)) {
    return false;
  }

  const lineType = normalizeText(item.line_type).toLowerCase();
  if (lineType === 'govt_funding_fee' || lineType.startsWith('govt_funding_fee_')) {
    return true;
  }

  const description = normalizeText(item.description);
  if (/\b(VA Funding Fee|Govt Funding Fee|Government Funding Fee|FHA UFMIP|USDA Guarantee Fee)\b/i.test(description)) {
    return true;
  }

  return /\bNet Mortgage Insurance\b/i.test(description) && isGovernmentBackedLoanType(loanType);
}

export function isRetainedClosingFeeLineItem(
  item: Pick<WarehouseCashFlowLineItem, 'category' | 'line_type'>,
  retainedClosingFeeBasis: RetainedClosingFeeBasis = 'legacy'
): boolean {
  const retainedFeeLineTypes = retainedClosingFeeBasis === 'expandedLeFees'
    ? EXPANDED_LE_FEE_RETAINED_CLOSING_FEE_LINE_TYPES
    : LEGACY_RETAINED_CLOSING_FEE_LINE_TYPES;

  return item.category === 'loan_revenue' && retainedFeeLineTypes.has(item.line_type);
}

export interface InvestorWireAuditInput {
  loanAmount: number;
  actualPurchasePricePercent: number;
  totalInvestorRevenue: number;
  totalInvestorCosts: number;
  servicingPrincipalCollected?: number;
  wireFromInvestor: number;
  tolerance?: number;
}

export interface InvestorWireAuditResult {
  rawExpectedInvestorWire: number;
  rawDifference: number;
  displayExpectedInvestorWire: number;
  displayDifference: number;
  isInvestorBalanced: boolean;
}

function normalizeDisplayZero(value: number): number {
  return Math.abs(value) <= Number.EPSILON ? 0 : value;
}

export function calculateInvestorWireAudit(input: InvestorWireAuditInput): InvestorWireAuditResult {
  const rawExpectedInvestorWire =
    input.loanAmount * (input.actualPurchasePricePercent / 100) +
    input.totalInvestorRevenue -
    input.totalInvestorCosts -
    (input.servicingPrincipalCollected || 0);
  const rawDifference = input.wireFromInvestor - rawExpectedInvestorWire;
  const tolerance = input.tolerance ?? MONEY_TOLERANCE;
  const isInvestorBalanced = Math.abs(rawDifference) <= tolerance;

  return {
    rawExpectedInvestorWire,
    rawDifference,
    displayExpectedInvestorWire: isInvestorBalanced ? input.wireFromInvestor : rawExpectedInvestorWire,
    displayDifference: isInvestorBalanced ? 0 : normalizeDisplayZero(rawDifference),
    isInvestorBalanced
  };
}

export interface WarehouseAdvanceInput {
  loanAmount: number;
  totalLoanRevenue?: number;
  processingFee?: number;
  underwritingFee?: number;
  interestIncome?: number;
  escrowDeposit: number;
  lenderCredit?: number;
  creditCureNettedToWarehouseAdvance?: number;
  governmentFundingPassThrough?: number;
  buydownPassThrough?: number;
  /** FHA UFMIP retained from closing for later lender-to-HUD remittance. */
  fhaUfmipWarehousePremium?: number;
  lineItems?: WarehouseCashFlowLineItem[];
  retainedClosingFeeBasis?: RetainedClosingFeeBasis;
  loanType?: string | null;
  haircutPercent: number;
  basisMode?: WarehouseBasisMode;
  cdCreditNettedToWarehouseAdvance?: number | null;
}

export interface ExpectedBalanceInput extends WarehouseAdvanceInput {
  wireFromInvestor: number;
  totalWarehouseCosts: number;
  shortageFunded: number;
  flexcloseCredit: number;
}

export interface ExpectedBalanceResult {
  feesAtClosing: number;
  retainedClosingFees: number;
  governmentFundingPassThrough: number;
  buydownPassThrough: number;
  fhaUfmipWarehousePremium: number;
  creditCureNettedToWarehouseAdvance: number;
  netFeesRetained: number;
  warehouseAdvance: number;
  warehouseCostsExShortage: number;
  /** Expected wire before manual adjustments (the existing cash-basis formula). */
  expectedBalanceBeforeAdjustments: number;
  /** Signed manual adjustments whose cash moved through the warehouse wire. */
  manualWireAdjustment: number;
  expectedBalance: number;
  warehouseDifference: number;
  isWarehouseBalanced: boolean;
  basisModeUsed: WarehouseBasisModeUsed;
  netPayableToLenderAtClosing: number | null;
  cashNeededAtClosing: number | null;
}

export interface WarehouseAdvanceBasis {
  retainedClosingFees: number;
  governmentFundingPassThrough: number;
  buydownPassThrough: number;
  fhaUfmipWarehousePremium: number;
  creditCureNettedToWarehouseAdvance: number;
  feesAtClosing: number;
}

export interface WarehouseCashBasisInput {
  basisMode?: WarehouseBasisMode;
  loanAmount: number;
  feesAtClosing: number;
  shortageFunded: number;
  haircutPercent: number;
  cdCreditNettedToWarehouseAdvance?: number | null;
}

export interface WarehouseCashBasisResolution {
  basisModeUsed: WarehouseBasisModeUsed;
  cdCreditNettedToWarehouseAdvance: number | null;
  netPayableToLenderAtClosing: number | null;
  cashNeededAtClosing: number | null;
}

function legacyWarehouseCashBasis(): WarehouseCashBasisResolution {
  return {
    basisModeUsed: 'legacy',
    cdCreditNettedToWarehouseAdvance: null,
    netPayableToLenderAtClosing: null,
    cashNeededAtClosing: null
  };
}

export function resolveWarehouseCashBasis(input: WarehouseCashBasisInput): WarehouseCashBasisResolution {
  if (input.basisMode !== 'autoCdCashRequired') {
    return legacyWarehouseCashBasis();
  }

  if (Math.abs(input.shortageFunded) > MONEY_TOLERANCE || Math.abs(input.haircutPercent) > MONEY_TOLERANCE) {
    return legacyWarehouseCashBasis();
  }

  if (!Number.isFinite(input.loanAmount) || !Number.isFinite(input.feesAtClosing)) {
    return legacyWarehouseCashBasis();
  }

  const cdCreditNettedToWarehouseAdvance = input.cdCreditNettedToWarehouseAdvance;
  if (
    cdCreditNettedToWarehouseAdvance === null ||
    cdCreditNettedToWarehouseAdvance === undefined ||
    !Number.isFinite(cdCreditNettedToWarehouseAdvance) ||
    cdCreditNettedToWarehouseAdvance < 0
  ) {
    return legacyWarehouseCashBasis();
  }

  const netPayableToLenderAtClosing = input.feesAtClosing - cdCreditNettedToWarehouseAdvance;
  const cashNeededAtClosing = input.loanAmount - netPayableToLenderAtClosing;

  return {
    basisModeUsed: 'cdCashRequired',
    cdCreditNettedToWarehouseAdvance,
    netPayableToLenderAtClosing,
    cashNeededAtClosing
  };
}

export interface CdCreditBasisInput {
  rawLenderCredit4794?: unknown;
  lenpcc?: unknown;
  persistedLenderCredit?: unknown;
  persistedLenderCreditIsCurrentPnlBasis?: boolean;
  cureAppliedLenderCredit?: unknown;
  lenderCreditNetsToWarehouseAdvance: boolean | null;
  cureAppliedLenderCreditNetsToWarehouseAdvance: boolean | null;
  /**
   * Legacy finalized reports used only field 4794 for lender-credit timing.
   * Current draft recon UI uses the visible lender-credit row total
   * (4794 + LENPCC), so past finalized projections remain unchanged unless a
   * caller explicitly opts into total-row timing.
   */
  lenderCreditTimingBasis?: 'field4794' | 'total';
}

export function calculateCdCreditNettedToWarehouseAdvance(input: CdCreditBasisInput): number | null {
  const rawLenderCredit4794 = toFiniteNumberOrNull(input.rawLenderCredit4794);
  const persistedLenderCredit = toFiniteNumberOrNull(input.persistedLenderCredit);
  const lenpcc = toFiniteNumberOrNull(input.lenpcc);
  const cureAppliedLenderCredit = toFiniteNumberOrNull(input.cureAppliedLenderCredit) ?? 0;
  const fallbackLenderCreditTotal = rawLenderCredit4794 !== null || lenpcc !== null
    ? (rawLenderCredit4794 ?? 0) + (lenpcc ?? 0)
    : null;
  const lenderCreditTotal = persistedLenderCredit ?? fallbackLenderCreditTotal;
  const lenderCreditTimingBasis = input.lenderCreditTimingBasis ?? 'field4794';
  let lenderCreditForTiming = rawLenderCredit4794;

  if (lenderCreditTimingBasis === 'total') {
    lenderCreditForTiming = lenderCreditTotal;
  } else if (lenderCreditForTiming === null && input.persistedLenderCreditIsCurrentPnlBasis === true) {
    if (persistedLenderCredit === null || lenpcc === null) {
      return null;
    }

    const derived = persistedLenderCredit - lenpcc;
    if (derived < -MONEY_TOLERANCE) {
      return null;
    }
    lenderCreditForTiming = Math.max(0, derived);
  }

  if (input.lenderCreditNetsToWarehouseAdvance === null && (lenderCreditForTiming ?? lenderCreditTotal ?? 0) > MONEY_TOLERANCE) {
    return null;
  }
  if (input.cureAppliedLenderCreditNetsToWarehouseAdvance === null && cureAppliedLenderCredit > MONEY_TOLERANCE) {
    return null;
  }

  let total = 0;

  if (input.lenderCreditNetsToWarehouseAdvance === true) {
    if (lenderCreditForTiming === null || lenderCreditForTiming < 0) {
      return null;
    }

    total += lenderCreditForTiming;
  }

  if (input.cureAppliedLenderCreditNetsToWarehouseAdvance === true) {
    if (cureAppliedLenderCredit < 0) {
      return null;
    }

    total += cureAppliedLenderCredit;
  }

  return total;
}

export function calculateWarehouseAdvanceBasis(input: WarehouseAdvanceInput): WarehouseAdvanceBasis {
  const hasDetailedFeeInputs =
    input.processingFee !== undefined ||
    input.underwritingFee !== undefined ||
    input.interestIncome !== undefined ||
    input.lineItems !== undefined;

  const retainedClosingFees = hasDetailedFeeInputs
    ? (input.processingFee || 0)
      + (input.underwritingFee || 0)
      + (input.interestIncome || 0)
      + (input.lineItems || [])
        // Single pass, each row once: allowlisted retained fee OR a row explicitly
        // flagged to net to the warehouse advance (auto-captured seller-paid Lendwise
        // fees + dropdown-added custom fees). Contribution = revenue amount + the
        // retained-but-not-revenue portion (warehouse_retained_amount).
        .filter((item) =>
          isRetainedClosingFeeLineItem(item, input.retainedClosingFeeBasis)
          || (item.category === 'loan_revenue' && item.nets_to_warehouse_advance === true)
        )
        .reduce((sum, item) => sum + item.amount + (item.warehouse_retained_amount || 0), 0)
    : (input.totalLoanRevenue || 0);

  const governmentFundingPassThrough = input.governmentFundingPassThrough ?? (input.lineItems || [])
    .filter((item) => isGovernmentFundingPassThrough(item, input.loanType))
    .reduce((sum, item) => sum + item.amount, 0);

  const buydownPassThrough = input.buydownPassThrough ?? (input.lineItems || [])
    .filter((item) => isBuydownPassThrough(item))
    .reduce((sum, item) => sum + item.amount, 0);
  const fhaUfmipWarehousePremium = input.fhaUfmipWarehousePremium || 0;

  const creditCureNettedToWarehouseAdvance = input.creditCureNettedToWarehouseAdvance ?? (
    input.lineItems
      ? input.lineItems
        .filter((item) =>
          item.category === 'lender_cost' &&
          (item.line_type === 'lender_credit' || item.line_type === 'cure_applied_lender_credit') &&
          item.nets_to_warehouse_advance === true
        )
        .reduce((sum, item) => sum + item.amount, 0)
      : (input.lenderCredit || 0)
  );

  const feesAtClosing = retainedClosingFees + input.escrowDeposit + governmentFundingPassThrough
    + buydownPassThrough + fhaUfmipWarehousePremium;

  return {
    retainedClosingFees,
    governmentFundingPassThrough,
    buydownPassThrough,
    fhaUfmipWarehousePremium,
    creditCureNettedToWarehouseAdvance,
    feesAtClosing
  };
}

/**
 * Calculate the warehouse advance amount.
 * Fees retained at closing reduce the advance; Non-QM haircut applied on top.
 */
export function calculateWarehouseAdvance(input: WarehouseAdvanceInput): {
  feesAtClosing: number;
  retainedClosingFees: number;
  governmentFundingPassThrough: number;
  buydownPassThrough: number;
  fhaUfmipWarehousePremium: number;
  creditCureNettedToWarehouseAdvance: number;
  netFeesRetained: number;
  warehouseAdvance: number;
} {
  const {
    retainedClosingFees,
    governmentFundingPassThrough,
    buydownPassThrough,
    fhaUfmipWarehousePremium,
    creditCureNettedToWarehouseAdvance,
    feesAtClosing
  } = calculateWarehouseAdvanceBasis(input);
  const netFeesRetained = Math.max(0, feesAtClosing - creditCureNettedToWarehouseAdvance);
  const warehouseAdvance = input.loanAmount * (1 - input.haircutPercent / 100) - netFeesRetained;

  return {
    feesAtClosing,
    retainedClosingFees,
    governmentFundingPassThrough,
    buydownPassThrough,
    fhaUfmipWarehousePremium,
    creditCureNettedToWarehouseAdvance,
    netFeesRetained,
    warehouseAdvance
  };
}

/**
 * Calculate the full expected balance for the Warehouse → Us wire reconciliation.
 */
export function calculateExpectedBalance(
  input: ExpectedBalanceInput,
  wireFromWarehouse: number
): ExpectedBalanceResult {
  const {
    feesAtClosing,
    retainedClosingFees,
    governmentFundingPassThrough,
    buydownPassThrough,
    fhaUfmipWarehousePremium,
    creditCureNettedToWarehouseAdvance,
    netFeesRetained,
    warehouseAdvance
  } = calculateWarehouseAdvance(input);
  const warehouseCostsExShortage = input.totalWarehouseCosts - input.shortageFunded;
  const cashBasis = resolveWarehouseCashBasis({
    basisMode: input.basisMode,
    loanAmount: input.loanAmount,
    feesAtClosing,
    shortageFunded: input.shortageFunded,
    haircutPercent: input.haircutPercent,
    cdCreditNettedToWarehouseAdvance: input.cdCreditNettedToWarehouseAdvance
  });
  const expectedBalanceBeforeAdjustments = cashBasis.basisModeUsed === 'cdCashRequired'
    ? input.wireFromInvestor -
      (cashBasis.cashNeededAtClosing ?? 0) -
      input.totalWarehouseCosts +
      input.flexcloseCredit
    : input.wireFromInvestor -
      warehouseAdvance -
      warehouseCostsExShortage -
      input.shortageFunded +
      input.flexcloseCredit;
  // Manual adjustments move the wire by their full signed amount after either cash-basis formula;
  // they never touch closing fees, the advance, the haircut or the legacy credit cap.
  const manualAdjustment = manualWireAdjustment(input.lineItems);
  const expectedBalance = expectedBalanceBeforeAdjustments + manualAdjustment;
  const warehouseDifference = wireFromWarehouse - expectedBalance;
  const isWarehouseBalanced = Math.abs(warehouseDifference) <= 0.01;

  return {
    feesAtClosing,
    retainedClosingFees,
    governmentFundingPassThrough,
    buydownPassThrough,
    fhaUfmipWarehousePremium,
    creditCureNettedToWarehouseAdvance,
    netFeesRetained,
    warehouseAdvance,
    warehouseCostsExShortage,
    expectedBalanceBeforeAdjustments,
    manualWireAdjustment: manualAdjustment,
    expectedBalance,
    warehouseDifference,
    isWarehouseBalanced,
    basisModeUsed: cashBasis.basisModeUsed,
    netPayableToLenderAtClosing: cashBasis.netPayableToLenderAtClosing,
    cashNeededAtClosing: cashBasis.cashNeededAtClosing
  };
}
